<script lang="ts" setup>
import {onMounted, ref, watch} from "vue";
import CommonDatepicker from "@/components/common/CommonDatepicker.vue";
import {SearchMd} from "utruckin-icons";
import CommonDropdown from "./CommonDropdown.vue";
import CustomSearch from "@/components/custom/CustomSearch.vue";
import CustomTab from "@/components/custom/CustomTab.vue";
import {IShift} from "@/features/shifts/shifts.types";
import {useShiftStore} from "@/features/shifts/shifts.store";
import {ICommonFilterProps, IDropdown} from "@/types/main";
import {useAppStore} from "@/store/app.store";
import {useAccountStore} from "@/features/account/account.store";
import {IAccount} from "@/features/account/account.types";
import {DateTime} from 'luxon'

const props = withDefaults(defineProps<ICommonFilterProps>(), {
  withSearch: true,
  withDate: true,
  withAccount: true,
  withOutgoing: true,
  withShift: true,
  loading: true,
  noDate: false,
});

const activeTab = ref<number>(0);
const search = ref<string>('');
let debounceTimer: ReturnType<typeof setTimeout> | null = null
// accounts
const accountType = ref(0);
const accountLoading = ref<boolean>(false);
const accounts = ref<IDropdown[]>([])
// shifts
const shiftLoading = ref<boolean>(false);
const shifts = ref<IDropdown[]>([])
const shiftType = ref(0);

const today = new Date()
const yesterday = new Date()
yesterday.setDate(today.getDate() - 1)

const shiftStore = useShiftStore()
const accountStore = useAccountStore()
const appStore = useAppStore()

const tabs = [
  {label: "All", value: 0},
  {label: "Sent", value: 1},
  {label: "Received", value: 2},
]

function datepickerToUtcDateOnly(d: Date | null | undefined) {
  if (!d) return ''
  const local = DateTime.fromJSDate(d)
  return DateTime.utc(local.year, local.month, local.day).toFormat('yyyy-MM-dd')
}

const handleDateUpdate = (startDate: Date, endDate: Date) => {
  props.store.commonFilter.date_from = datepickerToUtcDateOnly(startDate);
  props.store.commonFilter.date_to = datepickerToUtcDateOnly(endDate);

  props.store.commonFilter.page = 1
  props.store.commonFilter.page_size = 50
}

const getShifts = async () => {
  shifts.value = []

  shiftStore.shiftsForFilter = await shiftStore.getShifts(shiftLoading, true)

  if (shiftStore.shiftsForFilter.length) {
    shiftStore.shiftsForFilter.forEach((item: IShift) => {
      shifts.value.push({name: item.title, value: item.id});
    })

    shifts.value.unshift({name: "All shift", value: 0});
  }
}

const getAccounts = async () => {
  accounts.value = []

  accountStore.accountsForFilter = await accountStore.getAccounts(accountLoading)

  if (accountStore.accountsForFilter.length) {
    accountStore.accountsForFilter.forEach((item: IAccount) => {
      accounts.value.push({name: item.session_name, value: item.id});
    })

    accounts.value.unshift({name: "All accounts", value: 0});
  }
}

onMounted(() => {
  function getUtcDateOnlyFromLocal(dt: DateTime) {
    return DateTime
        .utc(dt.year, dt.month, dt.day)
        .toFormat('yyyy-MM-dd')
  }

  const yesterdayLocal = DateTime.local().minus({days: 1})

  if (props.store && props.withDate && !props.noDate) {
    props.store.commonFilter.date_from = getUtcDateOnlyFromLocal(yesterdayLocal)
    props.store.commonFilter.date_to = getUtcDateOnlyFromLocal(DateTime.local())
  }

  getShifts()
  getAccounts()
})

watch(
    () => appStore.refreshKey,
    () => {
      getShifts()
      getAccounts()
    }
)

watch(
    () => search.value,
    (newSearch) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
      }

      debounceTimer = setTimeout(() => {
        const value = newSearch?.trim() || ''

        if (value.length >= 4) {
          props.store.commonFilter.q = value
        } else if (value.length === 0) {
          props.store.commonFilter.q = ''
        } else {
          return
        }

        props.store.commonFilter.page = 1
        props.store.commonFilter.page_size = 50
      }, 500)
    }
)

watch(
    () => shiftType.value,
    (newShiftType) => {
      if (newShiftType == 0) {
        props.store.commonFilter.shift_id = null;
      } else {
        props.store.commonFilter.shift_id = newShiftType;
      }

      props.store.commonFilter.page = 1
      props.store.commonFilter.page_size = 50
    }, {
      immediate: false,
    }
)

watch(
    () => accountType.value,
    (newAccountType) => {
      if (newAccountType == 0) {
        props.store.commonFilter.account = null;
      } else {
        props.store.commonFilter.account = newAccountType;
      }

      props.store.commonFilter.page = 1
      props.store.commonFilter.page_size = 50
    }, {
      immediate: false,
    }
)

watch(
    () => activeTab.value,
    (newActiveTab) => {
      if (newActiveTab == 0) {
        props.store.commonFilter.outgoing = null;
      } else props.store.commonFilter.outgoing = newActiveTab == 1;

      props.store.commonFilter.page = 1
      props.store.commonFilter.page_size = 50
    }, {
      immediate: false,
    }
)
</script>

<template>
  <div class="c-co-header">
    <CustomSearch v-if="withSearch" v-model="search" :icon="SearchMd" placeholder="Users"/>

    <CommonDatepicker
        v-if="withDate"
        mode="range"
        :default-range="noDate ? {} : {
            fromDate: new Date(yesterday),
            toDate: new Date(today),
          }"
        :max-days-range="30"
        :is-two-sinded="true"
        placement="right"
        :clearable="true"
        @update="handleDateUpdate"
    />
    <CommonDropdown
        v-if="withAccount"
        v-model="accountType"
        label="Accounts"
        :items="accounts"
        :disabled="!accounts.length || accountLoading "
    />

    <CommonDropdown
        v-if="withShift"
        v-model="shiftType"
        label="All shift"
        :items="shifts"
        :disabled="!shifts.length || shiftLoading"
    />

    <CustomTab
        v-if="withOutgoing" :tabs="tabs" v-model="activeTab"/>
  </div>
</template>

<style lang="scss" scoped>
.c-co-header {
  display: flex;
  width: 100%;
  gap: 8px;
}
</style>
